import {observable, toJS} from 'mobx';
import FileSaver from 'file-saver';
import _ from 'lodash';

import {AnalyticsTab, Store} from './store';
import {DateRange, TimeState} from './TimeIntervalStore';
import {DateUtils} from '../utility/DateUtils';
import {HistoryHandler, HistoryItem} from './MemoryBarStore';
import {SelectorTableLayout} from './SelectorTableLayout';
import {moneyFormat, ValueFormat} from '../utility/FormatValueUtils';
import {TableSort, TableSortDirection} from '../types/Table';
import {TableDataForChannel, TableDataForChannelResponse, TableMode} from '../types/api/tableData/TableDataForChannel';
import {
    DescriptiveKPIAdditions,
    isKPI,
    isKpiGroupKeyOrDescriptiveKpiAdditions,
    isKpiWithGroupKeys,
    isProductSlopeOutlierKPI,
    isSearchSlopeOutlierKPI,
    isSessionInsightKPI,
    KPI,
    KPIGroupKey,
    ProductKPI,
    SearchtermKPI,
    SessionInsightKPI,
    tKeyKpi,
    tKeyKpiDescription,
    CampaignKPI,
} from '../kpi';
import {CompareMode} from './CompareStore';
import {Omit} from '../types/Utility';
import {getLanguage, MessageProvider} from '../i18n/i18n';
import {Channel} from '../types/Channel';
import {ChannelState} from './ChannelStore';
import DataVersionChecker, {DataVersionInfo} from '../utility/DataVersionChecker';
import * as ReportAPI from '../apis/ReportAPI';
import calculateSize from '../utility/calculate-size';

export interface CellAttributeContextMenu {
    x: number;
    y: number;
    channelCompareMode?: CompareMode;
    compareChannel?: string;
    tab: AnalyticsTab;
    primary?: string;
    secondary: KPI;
    sessionApiKey: ShowInsightsKPI;
}

export interface CompareColumnInfo {
    compareMode: CompareMode;
    targetName?: string;
}

export type DataKey = KPI | KPIGroupKey | DescriptiveKPIAdditions | NoneKPIColumns;

export const isDataKey = (value: any): value is DataKey => {
    return value && (isKpiWithGroupKeys(value) || isKpiGroupKeyOrDescriptiveKpiAdditions(value) || isNoneKPIColumn(value));
};

export interface SelectorTableColumnDefScoreAttributes {
    slope: KPI;
    outlier: KPI;
    maxSlope: KPI;
    minSlope: KPI;
}

export type ShowInsightsKPI =
    | SessionInsightKPI
    | 'sales'
    | 'clickCount'
    | 'searchEvents'
    | 'cartEvents'
    | 'cartTotalAmount'
    | 'checkoutEvents'
    | 'checkoutTotalAmount';

export interface SelectorTableColumnDef {
    dataKey: DataKey;
    compareColumnInfo?: CompareColumnInfo;
    label: string;
    minWidth: number;
    width: number;
    emptySumRow?: boolean;
    align: 'left' | 'right';
    valueFormat: ValueFormat;
    isRate?: boolean;
    isChartable?: boolean;
    usesMoneyFormat?: boolean;
    postfix?: string;
    sessionApiKey?: ShowInsightsKPI;
    description?: string;
    channelComparisonEnabled?: boolean; // default true
    withFilter?: boolean; //default false
    isSortable?: boolean; // default true;
    withRevealOnHover?: boolean;
    scoreAttributes?: SelectorTableColumnDefScoreAttributes;
    onIconClick?: (cellValue: string) => void;
}

export interface SelectorTableColumnDefs {
    [key: string]: SelectorTableColumnDef;
}

export interface FilterRule {
    operator: string;
    value: number;
}

export interface TableFilter {
    attribute: KPI;
    compareColumnInfo?: CompareColumnInfo;
    rules: FilterRule;
}

export interface BackendFilter {
    attribute: KPI;
    comparator: string;
    value: number;
    compareColumnInfo?: CompareColumnInfo;
}

export const isBackendFilter = (value: any): value is BackendFilter => {
    return value && typeof value.value === 'number' && typeof value.comparator === 'string' && isKPI(value.attribute);
};

export interface TableHeaderFilterMenuData {
    x: number;
    y: number;
    attribute: KPI;
    activeAnalyticsTab: string;
    compareColumnInfo?: CompareColumnInfo;
    isClosed?: boolean;
}

export enum NoneKPIColumns {
    RowNumber = 'rowNumberCell',
    Channel = 'channel',
}

export const isNoneKPIColumn = (value: any): value is NoneKPIColumns => {
    return Object.values(NoneKPIColumns).indexOf(value) > -1;
};

export interface TableDataForChannelWithRowNumber extends TableDataForChannel {
    [NoneKPIColumns.RowNumber]: string;
}

export interface TableData {
    [channel: string]: TableDataForChannelWithRowNumber[];
}

const reduceFilterForBackend = (filters: TableFilter[]): BackendFilter[] => {
    return filters.map((filter) => {
        return {
            attribute: filter.attribute,
            comparator: filter.rules.operator,
            value: filter.rules.value,
            compareColumnInfo: filter.compareColumnInfo,
        };
    });
};

abstract class GeneralSelectorTableStore implements HistoryHandler {
    public dataVersionChecker: DataVersionChecker;
    private recomputeMultiGridSize?: () => void;

    @observable public showLoadingHover = false;
    @observable public searchActive = false;
    @observable public searchterm = '';
    public useExactSearching = false;
    public tableData: TableData = {};

    @observable public activeTableFilters: TableFilter[] = [];

    @observable public cellAttributeContextMenu: CellAttributeContextMenu | null = null;

    public loadedChannels: string[] = [];
    @observable public searchResult = [];

    protected abstract dependantAnalyticsTab: AnalyticsTab;

    @observable public updateTable = 0;
    public channelSumRowInformation: {[key: string]: undefined | TableDataForChannel} = {};
    @observable public channelSumRowIsChartable = true;

    // Table header filter menu state
    @observable
    public tableHeaderFilterMenu: TableHeaderFilterMenuData = {
        x: 0,
        y: 0,
        attribute: SearchtermKPI.SearchCount,
        activeAnalyticsTab: AnalyticsTab.SearchView,
        isClosed: true,
    };

    @observable public updateChannelSumRow = 0;

    @observable public abstract tableSorting: TableSort;
    public abstract readonly tableColumnList: SelectorTableColumnDef[];
    public abstract readonly layout: SelectorTableLayout;

    public abstract fetchInsightsData(key: string, kpi: KPI, channel: Channel): void;

    public abstract readonly defaultSorting: TableSort;
    private tableColumnLookup?: SelectorTableColumnDefs;

    constructor(
        protected readonly mainStore: Store,
        public t: MessageProvider,
        protected readonly channelState: ChannelState,
        protected readonly timeState: TimeState
    ) {
        this.dataVersionChecker = new DataVersionChecker();
    }

    public tableHasDataKey(dataKey: DataKey): boolean {
        return this.tableColumns()[dataKey] !== undefined;
    }

    public setRecomputeMultiGridSize = (recomputeGridSize: () => void) => {
        this.recomputeMultiGridSize = recomputeGridSize;
    };

    public recomputeMultiGridSizeIfPossible = () => {
        if (this.recomputeMultiGridSize) {
            this.recomputeMultiGridSize();
        }
    };

    public getMoneyFormatter() {
        const {compareStore} = this.mainStore;
        if (compareStore) {
            const {compareData, comparisonTargetCurrency, compareIsEnabled} = compareStore;
            return moneyFormat(compareData && compareIsEnabled ? {currency: comparisonTargetCurrency} : this.mainStore.channelStore.selectedChannel);
        }
        return moneyFormat(this.mainStore.channelStore.selectedChannel);
    }

    public resizeColumns(tableColumns: Array<Omit<SelectorTableColumnDef, 'width'>>): SelectorTableColumnDef[] {
        return tableColumns.map((tableColumn) => ({
            ...tableColumn,
            width: Math.max(
                tableColumn.minWidth,
                calculateSize(tableColumn.label.toUpperCase(), {
                    fontSize: '14px',
                    font:
                        '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                }).width + 10
            ),
        }));
    }

    public getTableColumnForKpi(kpi: KPI): SelectorTableColumnDef | undefined {
        return this.tableColumnList.find((tableColumnList) => tableColumnList.dataKey === kpi);
    }

    public withI18N = (
        items: Array<Omit<SelectorTableColumnDef, 'description' | 'label' | 'width'>>
    ): Array<Omit<SelectorTableColumnDef, 'width'>> => {
        return items.map((item) => {
            if (isKpiWithGroupKeys(item.dataKey)) {
                return {
                    ...item,
                    description: this.t(tKeyKpiDescription(item.dataKey)),
                    label: this.t(tKeyKpi(item.dataKey)),
                };
            }
            if (item.dataKey === NoneKPIColumns.RowNumber) {
                return {...item, description: '', label: '#'};
            }
            throw new Error('cannot translate ' + item.dataKey);
        });
    };

    public getChartableColumnsForKpiSwitcher = (): Array<{value: DataKey; text: string}> =>
        this.tableColumnList
            .filter((columnListItem) => columnListItem.isChartable)
            .map((columnListItem) => ({
                value: columnListItem.dataKey,
                text: columnListItem.label,
            }));

    private refreshTableColumnLookup() {
        this.tableColumnLookup = {};
        this.tableColumnList.forEach((item) => (this.tableColumnLookup![item.dataKey] = item));
    }

    public tableColumns = (): SelectorTableColumnDefs => {
        if (!this.tableColumnLookup) {
            this.refreshTableColumnLookup();
        }
        return this.tableColumnLookup!;
    };

    public getLabelForKpi = (kpi: KPI) => {
        const tableColumns = this.tableColumns();
        return tableColumns[kpi] ? tableColumns[kpi].label : '';
    };

    public getPartialHistory = (): Partial<HistoryItem> => ({
        activeSearch: this.searchterm,
        activeTableSort: this.tableSorting,
        activeFilters: reduceFilterForBackend(this.activeTableFilters).slice(),
    });

    public applyHistory = (history: HistoryItem): void => {
        if (this.dependantAnalyticsTab !== history.analyticsTab) {
            return;
        }
        const tableColumns = this.tableColumns();

        this.searchterm = history.activeSearch;
        this.useExactSearching = false;

        if (history.activeTableSort) {
            this.setTableSortWithoutReload(
                history.activeTableSort.attribute,
                history.activeTableSort.sortDirection,
                history.activeTableSort.compareMode,
                history.activeTableSort.targetName
            );
        } else {
            this.setTableSortWithoutReload(this.defaultSorting.attribute, this.defaultSorting.sortDirection);
        }

        this.activeTableFilters = history.activeFilters
            .filter((filter) => tableColumns[filter.attribute])
            .map(
                (filter): TableFilter => ({
                    attribute: filter.attribute,
                    compareColumnInfo: filter.compareColumnInfo,
                    rules: {
                        operator: filter.comparator,
                        value: filter.value,
                    },
                })
            );
    };

    private applyTableSorting() {
        /**
         * Just applies the provided search matches
         */
        // reload data from db with sorting applied
        this.showLoadingHover = true;
        this.updateTableView();
        this.loadTableDataForSingleChannel(this.channelState.selectedChannel);
    }

    public setTableSortWithoutReload = (
        sortedColumn?: DataKey | 'DEFAULT',
        sortDirection?: TableSortDirection | 'TOGGLE',
        compareMode?: CompareMode,
        targetName?: string
    ) => {
        /**
         * Applies searches or sorted table data to the table
         */

        if (sortedColumn === 'DEFAULT') {
            sortedColumn = this.defaultSorting.attribute;
        }

        const isSlopeOrOutlier = (): boolean => {
            switch (this.dependantAnalyticsTab) {
                case AnalyticsTab.SearchView:
                    return isSearchSlopeOutlierKPI(sortedColumn);
                case AnalyticsTab.ProductView:
                    return isProductSlopeOutlierKPI(sortedColumn);
                default:
                    return false;
            }
        };

        if (sortedColumn && sortDirection && (this.tableHasDataKey(sortedColumn) || isSlopeOrOutlier())) {
            if (
                this.tableSorting.attribute === sortedColumn &&
                (sortDirection === TableSortDirection.ASC ||
                    (this.tableSorting.sortDirection === TableSortDirection.DESC && sortDirection === 'TOGGLE'))
            ) {
                sortDirection = TableSortDirection.ASC;
            } else {
                sortDirection = TableSortDirection.DESC;
            }
        } else {
            sortedColumn = this.defaultSorting.attribute;
            sortDirection = this.defaultSorting.sortDirection;
        }

        if (!isKpiWithGroupKeys(sortedColumn)) {
            throw Error(`Only KPI's are sortable, not ${sortedColumn}`);
        }

        this.tableSorting = {
            sortDirection,
            targetName,
            compareMode,
            attribute: sortedColumn,
        };

        this.saveTableSortingToLocalStorage(this.storeName());
    };

    public setTableSort(
        sortedColumn?: DataKey | 'DEFAULT',
        sortDirection?: TableSortDirection | 'TOGGLE',
        compareMode?: CompareMode,
        compareChannel?: string
    ) {
        this.setTableSortWithoutReload(sortedColumn, sortDirection, compareMode, compareChannel);
        this.applyTableSorting();
    }

    public setDirectTableSort(sortedColumn?: KPI, sortDirection?: TableSortDirection) {
        if (sortedColumn && sortDirection) {
            this.tableSorting = {
                sortDirection,
                attribute: sortedColumn,
            };
        } else {
            this.tableSorting = {
                sortDirection: this.defaultSorting.sortDirection,
                attribute: this.defaultSorting.attribute,
            };
        }

        this.applyTableSorting();
    }

    public loadTableData() {
        /**
         * Get Table data and the necessary channel data
         * @type {CampaignSelectorTableStore}
         */
        const {selectedChannel} = this.channelState;
        this.refreshTableColumnLookup();
        this.layout.updateVisibleColumns();
        if (selectedChannel) {
            this.loadTableDataForSingleChannel(selectedChannel);
        }
    }

    public runSearch(searchterm: string) {
        /**
         * Runs a searchterm through the channel table data
         * @type {string}
         */
        this.searchterm = searchterm;
        this.useExactSearching = false;
        if (!this.dataVersionChecker.checkIfDataVersionAlreadyExists(this.getCurrentDataVersionInfo())) {
            this.showLoadingHover = true;
            this.updateTableView();
            this.loadTableDataForSingleChannel(this.channelState.selectedChannel);
        }
    }

    private storeName(): TableMode {
        switch (this.mainStore.activeAnalyticsTab) {
            case AnalyticsTab.SearchView:
                return 'searchterm';
            case AnalyticsTab.ProductView:
                return 'product';
            case AnalyticsTab.CampaignView:
                return 'campaign';
            default:
                throw new Error('Unknown analytics tab');
        }
    }

    private generateCsvExportFileName() {
        const activeAnalyticsTab = this.mainStore.activeAnalyticsTab;

        let prefix = '';
        if (activeAnalyticsTab === AnalyticsTab.ProductView) {
            prefix += this.t('table:products');
        } else if (activeAnalyticsTab === AnalyticsTab.SearchView) {
            prefix += this.t('table:searchterms');
        } else if (activeAnalyticsTab === AnalyticsTab.CampaignView) {
            prefix += this.t('table:campaigns');
        }

        const selectedChannel = this.channelState.selectedChannel;
        const channel = selectedChannel ? selectedChannel.name : 'Unnamed';
        const {startDate, endDate} = this.timeState.mainDateRange;
        const start = startDate ? startDate.format('L') : 'NoStart';
        const end = endDate ? endDate.format('L') : 'NoEnd';

        return `${prefix} ${channel} ${start} - ${end}.csv`;
    }

    public generateTableDataCsvFormat = (seperator: string) => {
        const storeName = this.storeName();
        if (!this.channelState.selectedChannel || storeName === 'campaign') {
            return;
        }
        const {mainDateRange} = this.timeState;
        ReportAPI.downloadTableCsv({
            mode: storeName === 'searchterm' ? 'searchterm' : 'product',
            channel: this.channelState.selectedChannel.name,
            interval: DateUtils.toDailyDateRange(mainDateRange),
            language: getLanguage() || 'en',
            sorting: this.tableSorting,
            separator: seperator,
        })
            .then((data) => {
                const blob = new Blob([data.data], {type: 'application/vnd.ms-excel'}); // get file header for csv
                FileSaver.saveAs(blob, this.generateCsvExportFileName());
            })
            .catch(() => {
                /*noop*/
            });
    };

    public generateTableDataCsvSemicolonFormat = () => this.generateTableDataCsvFormat(';');

    public generateTableDataCsvCommaFormat = () => this.generateTableDataCsvFormat(',');

    public changeChannel(newChannel: Channel) {
        /**
         * Changes the active channel tab and reapplies the search, if one is active
         */
        this.channelState.setSelectedChannel(newChannel);
        this.layout.updateVisibleColumns();
    }

    public closeOpenTableDropdowns() {
        this.cellAttributeContextMenu = null;
    }

    private updateTableView() {
        this.updateTable++;
    }

    protected saveTableLayoutToLocalStorage = (storeName: TableMode) => {
        return (layoutState: string) => window.localStorage.setItem(storeName + '-tableLayout', layoutState);
    };

    /**
     * The visibility of columns gets currently saved in the localStorage of the browser.
     * If data like that exists in the localStorage, the default will be overwritten by them.
     */
    private updateTableLayoutFromLocalStorage = (storeName: TableMode) => {
        this.layout.setLayoutState(this.getTableLayoutFromLocalStorage(storeName));
    };

    protected getTableLayoutFromLocalStorage = (storeName: TableMode): string => {
        return window.localStorage.getItem(storeName + '-tableLayout') || '';
    };

    protected saveTableSortingToLocalStorage = (storeName: TableMode) => {
        const setItem = (key: string, value: string | undefined) => {
            window.localStorage.setItem(storeName + '-' + key, value ?? '');
        };
        setItem('tableSorting-sortDirection', this.tableSorting.sortDirection);
        setItem('tableSorting-attribute', this.tableSorting.attribute);
    };

    private updateTableSortingFromLocalStorage = (storeName: TableMode) => {
        const getItem = (key: string) => window.localStorage.getItem(storeName + '-' + key);

        const tableSortingSortDirection = getItem('tableSorting-sortDirection');
        const tableSortingAttribute = getItem('tableSorting-attribute');

        if (tableSortingAttribute === null) {
            return;
        }

        const isKpiValue = (kpi: any, value: string) => Object.values(kpi).indexOf(value) >= 0;
        if (storeName === 'searchterm' && !isKpiValue(SearchtermKPI, tableSortingAttribute)) {
            return;
        }
        if (storeName === 'campaign' && !isKpiValue(CampaignKPI, tableSortingAttribute)) {
            return;
        }
        if (storeName === 'product' && !isKpiValue(ProductKPI, tableSortingAttribute)) {
            return;
        }

        this.tableSorting.attribute = tableSortingAttribute as KPI;
        this.tableSorting.sortDirection = tableSortingSortDirection as TableSortDirection;
    };

    protected updateTableSettingsFromLocalStorage = (storeName: TableMode) => {
        this.updateTableLayoutFromLocalStorage(storeName);
        this.updateTableSortingFromLocalStorage(storeName);
    };

    /**
     * Returns true, if all request params are still in sync with the current state of frontend variables
     */
    private tableRequestParamsAreStillValid(params: {
        channel: Channel;
        mode: string;
        interval: DateRange;
        search: string;
        searchExactly: boolean;
        sorting?: TableSort;
        limit?: number;
        filter?: Array<{attribute: KPI; comparator: string; value: number}>;
    }) {
        const sortingValid = params.sorting ? _.isEqual(toJS(params.sorting), toJS(this.tableSorting)) : true;
        const filterValid = params.filter ? _.isEqual(params.filter, reduceFilterForBackend(this.activeTableFilters)) : true;
        return (
            params.search === this.searchterm &&
            this.useExactSearching === params.searchExactly &&
            sortingValid &&
            DateUtils.dateRangeIsSame(params.interval, this.mainStore.timeIntervalStore.mainDateRange) &&
            filterValid
        );
    }

    private addRowNumbersToTableData(tableDataResponse: TableDataForChannelResponse[]): TableDataForChannelWithRowNumber[] {
        return tableDataResponse.map((singleTableDataResponse, index) => ({
            ...singleTableDataResponse,
            [NoneKPIColumns.RowNumber]: String(index + 1),
        }));
    }

    private getCurrentDataVersionInfo(): DataVersionInfo {
        const {compareData, comparisonTargetCurrency} = this.mainStore.compareStore;
        const {selectedChannel} = this.channelState;
        const {mainDateRange, compareDateRange} = this.timeState;

        const getCompare = () => {
            const {channelCompare, variantChannelCompare} = compareData;
            if (channelCompare && channelCompare.channelNames.length > 0 && comparisonTargetCurrency) {
                return {
                    channelCompareNames: [...channelCompare.channelNames],
                    channelCompareTargetCurrency: comparisonTargetCurrency,
                };
            } else if (variantChannelCompare && comparisonTargetCurrency) {
                return {
                    testId: variantChannelCompare.testId,
                    variantIds: variantChannelCompare.variantIds,
                    channelCompareTargetCurrency: comparisonTargetCurrency,
                };
            }
            return {};
        };
        return {
            channel: selectedChannel,
            sortDirection: this.tableSorting.sortDirection,
            sortAttribute: this.tableSorting.attribute,
            searchterm: this.searchterm,
            searchExactly: this.useExactSearching,
            compareDateRange: DateUtils.copyDateRange(compareDateRange),
            mainDateRange: DateUtils.copyDateRange(mainDateRange),
            tableFilter: reduceFilterForBackend(this.activeTableFilters),
            ...getCompare(),
        };
    }

    private loadTableDataForSingleChannel(channel?: Channel) {
        if (!channel) {
            return;
        }

        const {searchterm} = this;

        if (this.dataVersionChecker.checkIfDataVersionAlreadyExists(this.getCurrentDataVersionInfo())) {
            return;
        }

        this.showLoadingHover = true;
        this.updateTableView();

        this.dataVersionChecker.addDataVersionInfo(this.getCurrentDataVersionInfo());

        const activeFilters = reduceFilterForBackend(this.activeTableFilters);

        this.loadDataForStickySumRow(channel, activeFilters);

        const {mainDateRange} = this.mainStore.timeIntervalStore;

        const tableDataForChannelRequestParams = {
            channel,
            mode: this.storeName(),
            interval: mainDateRange,
            search: searchterm,
            searchExactly: this.useExactSearching,
            sorting: this.tableSorting,
            limit: 1000,
            filter: activeFilters,
        };

        this.mainStore.compareStore
            .tableDataForChannel({
                ...tableDataForChannelRequestParams,
                channel: tableDataForChannelRequestParams.channel.name,
                filter: JSON.stringify(tableDataForChannelRequestParams.filter),
            })
            .then((data) => {
                if (!this.tableRequestParamsAreStillValid(tableDataForChannelRequestParams)) {
                    return;
                }

                this.tableData[channel.name] = this.addRowNumbersToTableData(data);

                if (this.loadedChannels.indexOf(channel.name) === -1) {
                    this.loadedChannels.push(channel.name);
                }
                this.showLoadingHover = false;
                this.updateTableView();
            })
            .catch((resp) => {
                console.log('error while fetching channel data', resp);
                this.tableData[channel.name] = [];
                if (this.loadedChannels.indexOf(channel.name) === -1) {
                    this.loadedChannels.push(channel.name);
                }
                this.showLoadingHover = false;
                this.updateTableView();
            });
    }

    private loadDataForStickySumRow(channel: Channel, activeFilters: BackendFilter[]) {
        const {searchterm} = this;
        const variantCompareActive = this.mainStore.compareStore.compareData.variantChannelCompare !== undefined;

        const stickyRowParams = {
            channel,
            mode: this.storeName(),
            interval: this.mainStore.timeIntervalStore.mainDateRange,
            filter: activeFilters,
            search: searchterm,
            searchExactly: this.useExactSearching,
            sorting: this.tableSorting,
        };

        this.mainStore.compareStore
            .stickyRowSums({
                ...stickyRowParams,
                channel: stickyRowParams.channel.name,
                filter: JSON.stringify(stickyRowParams.filter),
            })
            .then((data) => {
                if (!this.tableRequestParamsAreStillValid(stickyRowParams)) {
                    return;
                }

                if (data[0] !== undefined) {
                    this.channelSumRowInformation[channel.name] = this.calculateRatesForStickySumRow(
                        data[0],
                        channel,
                        searchterm,
                        variantCompareActive
                    );
                    this.forceUpdateChannelSumRow();
                } else {
                    this.channelSumRowInformation[channel.name] = undefined;
                    this.forceUpdateChannelSumRow();
                }
            })
            .catch(() => {
                /* no op */
            });
    }

    public triggerTableHeaderFilterMenu = (filterMenuPackage?: TableHeaderFilterMenuData) => {
        if (!filterMenuPackage || (filterMenuPackage.attribute === this.tableHeaderFilterMenu.attribute && !this.tableHeaderFilterMenu.isClosed)) {
            this.tableHeaderFilterMenu = {...this.tableHeaderFilterMenu, isClosed: true};
        } else {
            this.tableHeaderFilterMenu = filterMenuPackage;
        }
    };

    public applyFilterRules = (filterRules: FilterRule[], kpi: KPI, compareColumnInfo?: CompareColumnInfo) => {
        const cleanActiveTableFilter = this.activeTableFilters.filter((filter) => {
            if (filter.compareColumnInfo) {
                if (!compareColumnInfo) {
                    return false;
                }
                const channelMatch = filter.compareColumnInfo.targetName
                    ? filter.compareColumnInfo.targetName === compareColumnInfo.targetName
                    : true;
                const compareModesMatch = filter.compareColumnInfo.compareMode === compareColumnInfo.compareMode;
                return !(filter.attribute === kpi && channelMatch && compareModesMatch);
            }
            return filter.attribute !== kpi;
        });

        const newTableFilter = filterRules.map((filterRule) => {
            return {
                attribute: kpi,
                compareColumnInfo,
                rules: filterRule,
            };
        });

        this.activeTableFilters = [...cleanActiveTableFilter, ...newTableFilter];

        this.loadTableDataForSingleChannel(this.channelState.selectedChannel);
    };

    private forceUpdateChannelSumRow() {
        this.updateChannelSumRow++;
    }

    public getScoreAttribute(attribute: KPI, type: keyof SelectorTableColumnDefScoreAttributes) {
        const columnForAttribute = this.tableColumns()[attribute];
        if (columnForAttribute !== undefined && columnForAttribute.scoreAttributes !== undefined) {
            return columnForAttribute.scoreAttributes[type];
        }
        return undefined;
    }

    public abstract calculateRatesForStickySumRow(
        data: TableDataForChannel,
        channel: Channel,
        searchterm: string,
        variantCompareActive: boolean
    ): TableDataForChannel;

    public getAttributeNameIfPossible = (attribute: KPI): string => {
        const {selectorTableStoreProduct, selectorTableStoreSearch} = this.mainStore.getSearchtermAndProductSelectorTableStore();
        const columnInfoSearch = selectorTableStoreSearch.tableColumns()[attribute];
        if (columnInfoSearch !== undefined) {
            return columnInfoSearch.label;
        }
        const columnInfoProduct = selectorTableStoreProduct.tableColumns()[attribute];
        if (columnInfoProduct !== undefined) {
            return columnInfoProduct.label;
        }
        return attribute;
    };

    public checkIfKpiIsRate(kpi: KPI): boolean {
        const matchingColumn = this.tableColumnList.find((column) => column.dataKey === kpi);
        if (matchingColumn) {
            return matchingColumn.isRate === true;
        }
        return false;
    }

    public getSessionApiKeyIfPossible(originalKpi: ShowInsightsKPI): SessionInsightKPI {
        if (isSessionInsightKPI(originalKpi)) {
            return originalKpi;
        }

        throw Error('Tried to convert none SessionInsight KPI ' + originalKpi);
    }

    public checkIfKpiRequiresMoneyFormat = (kpi: KPI): boolean => {
        const tableColumnsRegister = this.tableColumns();
        return (tableColumnsRegister[kpi] && tableColumnsRegister[kpi].usesMoneyFormat) || false;
    };
}

export default GeneralSelectorTableStore;



// WEBPACK FOOTER //
// ./src/stores/GeneralSelectorTableStore.ts